import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { StakeHolderId } from '@pages/onboarding/Mexico/constants/stakeHolderOptions';
import {
  IDocumentTypeId,
  IDocumentUsaTypeId,
} from '@efex-services/documents/types';
import { IOnboardingDocuments } from '@efex-services/documents/documentsSvc';
import clientSvc from '@efex-services/client/clientSvc';
import { useApiCall } from 'use-api-call';
import {
  IStakeholderRequest,
  IStakeholderResponse,
} from '@pages/onboarding/types/newStakeholderResponse';
import { UseApiCallProps } from '@pages/lending/types';
import { useSelector } from 'react-redux';
import { selectBusiness } from '@pages/profile/store/selectors';
import useStakeholderFileService from '@efex-components/EfexDS/FileInput/useStakeholderFileService';
import {
  DeleteGovernmentIdDocument,
  GovermentIdReducerCases,
} from './reducers/govermentIdReducer/types';
import govermentIdReducer from './reducers/govermentIdReducer';
import StakeHolderContext from './StakeHolderContext';
import proofOfAddressDocumentReducer from './reducers/proofOfAddressDocumentReducer';
import { ProofOfAddressDocumentReducerCases } from './reducers/proofOfAddressDocumentReducer.types';

interface StakeHolderProviderProps {
  children: React.ReactNode;
}

const StakeHolderProvider = ({ children }: StakeHolderProviderProps) => {
  const [personType, setPersonType] = useState(StakeHolderId.NATURAL_MX);
  const [businessSectorId, setBusinessSectorId] = useState<
    ICompanySector | null | undefined
  >(null);
  const [proofOfAddressDocument, dispatchProofOfAddressDocument] = useReducer(
    proofOfAddressDocumentReducer,
    []
  );
  const [govermentIdDocuments, dispatchGovermentIdDocuments] = useReducer(
    govermentIdReducer,
    {
      documentOne: null,
      documentTwo: null,
    }
  );

  const company = useSelector(selectBusiness);

  const {
    invoke: getStakeHolderV2,
  }: UseApiCallProps<IStakeholderResponse, IStakeholderRequest> = useApiCall(
    () =>
      clientSvc.getStakeHolderV2({ enterpriseId: company?.enterpriseId ?? '' })
  );

  const {
    uploadedFiles,
    handleChangeFile,
    handleDeleteFile,
    handleDropChangeFile,
  } = useStakeholderFileService({ getStakeHolder: getStakeHolderV2 });

  const documentsToDeleteInBE = useRef<number[]>([]);

  const deleteDocumensToDelete = async () => {
    await Promise.all(
      documentsToDeleteInBE.current.map((documentId) =>
        handleDeleteFile(documentId)
      )
    );
  };

  const addDocumentToDelete = (documentId: number) => {
    documentsToDeleteInBE.current.push(documentId);
  };

  const setProofOfAddressDocument = (data: IOnboardingDocuments[]) => {
    dispatchProofOfAddressDocument({
      type: ProofOfAddressDocumentReducerCases.SET_DOCUMENT,
      payload: data,
    });
  };

  const changeProofOfAddressDocument = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleChangeFile(e);
  };

  const handleDropChangeDocument = (e: FileList, inputName: string) => {
    handleDropChangeFile(e, inputName);
  };

  const deleteProofOfAddressDocument = () => {
    const documentId = proofOfAddressDocument[0]?.id;

    dispatchProofOfAddressDocument({
      type: ProofOfAddressDocumentReducerCases.DELETE_DOCUMENT,
    });

    if (documentId) {
      addDocumentToDelete(documentId);
    }
  };

  const addFirstGovernmentIdDocument = (document: IOnboardingDocuments) => {
    dispatchGovermentIdDocuments({
      type: GovermentIdReducerCases.SET_FIRST_DOCUMENT,
      payload: [document],
    });
  };

  const addSecondGovernmentIdDocument = (document: IOnboardingDocuments) => {
    dispatchGovermentIdDocuments({
      type: GovermentIdReducerCases.SET_SECOND_DOCUMENT,
      payload: [document],
    });
  };

  const setGovermentIdDocuments = (data: IOnboardingDocuments[]) => {
    const documents = data.filter(
      (document) =>
        document.document.type.id !== IDocumentTypeId.PROOF_OF_ADDRESS
    );

    dispatchGovermentIdDocuments({
      type: GovermentIdReducerCases.SET_DOCUMENTS,
      payload: documents,
    });
  };

  const handleChangeTypeOfGovernmentId = () => {
    const documentIds = Object.values(govermentIdDocuments).map(
      (document) => document?.id
    );

    if (documentIds.length) {
      documentIds.forEach((documentId) => {
        if (!documentId) return;
        addDocumentToDelete(documentId);
      });
    }

    setGovermentIdDocuments([]);
  };

  const deleteGovernmentIdDocument = (
    documentPlace: DeleteGovernmentIdDocument
  ) => {
    dispatchGovermentIdDocuments({
      type: GovermentIdReducerCases.DELETE_DOCUMENTS,
      payload: documentPlace,
    });

    const documentId =
      documentPlace === 'first'
        ? govermentIdDocuments.documentOne?.id
        : govermentIdDocuments.documentTwo?.id;

    if (documentId) {
      addDocumentToDelete(documentId);
    }
  };

  const changeDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChangeFile(e);
  };

  const getAllCurrentDocumentsIds = () => {
    const idsArray: number[] = [];

    if (proofOfAddressDocument.length) {
      idsArray.push(proofOfAddressDocument[0].id);
    }

    Object.values(govermentIdDocuments).forEach(
      (document) => document && idsArray.push(document.id)
    );

    return idsArray;
  };

  useEffect(() => {
    const lastDocument = uploadedFiles[uploadedFiles.length - 1];

    if (!lastDocument) return;

    if (lastDocument.document.type.id === IDocumentTypeId.PROOF_OF_ADDRESS) {
      setProofOfAddressDocument([lastDocument]);
      return;
    }

    if (
      [IDocumentUsaTypeId.PASSPORT, IDocumentUsaTypeId.PERSONAL_ID].includes(
        lastDocument.document.type.id
      )
    ) {
      addFirstGovernmentIdDocument(lastDocument);
      return;
    }

    if (
      [IDocumentUsaTypeId.PERSONAL_ID_BACK].includes(
        lastDocument.document.type.id
      )
    )
      addSecondGovernmentIdDocument(lastDocument);
  }, [uploadedFiles]);

  const providerValue = useMemo(
    () => ({
      personType,
      setPersonType,
      businessSectorId,
      setBusinessSectorId,
      govermentIdDocuments,
      setGovermentIdDocuments,
      handleChangeTypeOfGovernmentId,
      deleteGovernmentIdDocument,
      changeDocument,
      deleteDocumensToDelete,
      proofOfAddressDocument,
      setProofOfAddressDocument,
      changeProofOfAddressDocument,
      deleteProofOfAddressDocument,
      handleDropChangeDocument,
      getAllCurrentDocumentsIds,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [personType, businessSectorId, govermentIdDocuments, proofOfAddressDocument]
  );

  return (
    <StakeHolderContext.Provider value={providerValue}>
      {children}
    </StakeHolderContext.Provider>
  );
};

export default StakeHolderProvider;
